export const getGridColsClass = (
  colsNumberOnDesktop = 2,
  colsNumberOnMobile?: number,
  desktopBreakpoint: 'md' | 'lg' = 'md'
) => {
  let className = ''

  const colsNumberOnMobileValue =
    colsNumberOnMobile || (colsNumberOnDesktop === 1 ? 1 : 2)

  switch (colsNumberOnMobileValue) {
    case 1:
      className += 'grid-cols-1 '
      break
    case 2:
      className += 'grid-cols-2 '
      break
    case 3:
      className += `grid-cols-3 `
      break
    case 4:
      className += 'grid-cols-4 '
      break
    case 5:
      className += 'grid-cols-5 '
      break
    case 6:
      className += 'grid-cols-6 '
      break
  }

  switch (colsNumberOnDesktop) {
    case 1:
      className +=
        desktopBreakpoint === 'lg' ? 'lg:grid-cols-1' : 'md:grid-cols-1'
      break
    case 2:
      className +=
        desktopBreakpoint === 'lg' ? 'lg:grid-cols-2' : 'md:grid-cols-2'
      break
    case 3:
      className +=
        desktopBreakpoint === 'lg' ? 'lg:grid-cols-3' : 'md:grid-cols-3'
      break
    case 4:
      className +=
        desktopBreakpoint === 'lg' ? 'lg:grid-cols-4' : 'md:grid-cols-4'
      break
    case 5:
      className +=
        desktopBreakpoint === 'lg' ? 'lg:grid-cols-5' : 'md:grid-cols-5'
      break
    case 6:
      className +=
        desktopBreakpoint === 'lg' ? 'lg:grid-cols-6' : 'md:grid-cols-6'
      break
  }

  return className
}

const baseGridColsClasses: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
}

const smGridColsClasses: Record<number, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
  5: 'sm:grid-cols-5',
  6: 'sm:grid-cols-6',
}

const lgGridColsClasses: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
}

const normalizeGridColsNumber = (cols: number, fallback: number) => {
  const normalizedCols = Math.floor(Number(cols) || fallback)

  return Math.min(6, Math.max(1, normalizedCols))
}

const constrainMediumGridColsNumber = (
  cols: number,
  desktopCols: number,
  mobileCols: number
) => Math.min(desktopCols - 1, Math.max(mobileCols, cols))

export const shouldUseMediumGridLayout = (
  colsNumberOnDesktop: number,
  colsNumberOnMobile: number,
  mediumColsMode: 'auto' | 'manual' = 'auto'
) => {
  if (mediumColsMode === 'manual') {
    return true
  }

  const desktopCols = normalizeGridColsNumber(colsNumberOnDesktop, 2)
  const mobileCols = normalizeGridColsNumber(
    colsNumberOnMobile,
    desktopCols === 1 ? 1 : 2
  )

  return desktopCols >= 4 && mobileCols < desktopCols
}

export const getMediumGridColsNumber = (
  cardsNumber: number,
  colsNumberOnDesktop: number,
  colsNumberOnMobile: number
) => {
  const desktopCols = normalizeGridColsNumber(colsNumberOnDesktop, 4)
  const mobileCols = normalizeGridColsNumber(colsNumberOnMobile, 2)
  const cardsCount = Math.max(0, Math.floor(Number(cardsNumber) || 0))

  if (cardsCount === 1) {
    return constrainMediumGridColsNumber(
      Math.round(desktopCols / 1.5),
      desktopCols,
      mobileCols
    )
  }

  const cardsInDesktopLastRow = cardsCount % desktopCols || desktopCols
  const isDesktopLastRowWellFilled =
    cardsInDesktopLastRow >= Math.ceil(desktopCols / 2)

  if (!isDesktopLastRowWellFilled) {
    return constrainMediumGridColsNumber(
      desktopCols - 1,
      desktopCols,
      mobileCols
    )
  }

  if (
    cardsCount > 0 &&
    cardsCount % 3 === 0 &&
    mobileCols <= 3 &&
    desktopCols > 3
  ) {
    return constrainMediumGridColsNumber(3, desktopCols, mobileCols)
  }

  for (let cols = desktopCols - 1; cols >= mobileCols; cols -= 1) {
    const cardsInLastRow = cardsCount % cols || cols

    if (cardsInLastRow >= Math.ceil(cols / 2)) {
      return cols
    }
  }

  return mobileCols
}

export const getAdaptiveGridColsClass = (
  cardsNumber: number,
  colsNumberOnDesktop = 2,
  colsNumberOnMobile?: number,
  mediumColsMode: 'auto' | 'manual' = 'auto',
  colsNumberOnMedium?: number
) => {
  const desktopCols = normalizeGridColsNumber(colsNumberOnDesktop, 2)
  const mobileCols = normalizeGridColsNumber(
    colsNumberOnMobile || 0,
    desktopCols === 1 ? 1 : 2
  )

  if (!shouldUseMediumGridLayout(desktopCols, mobileCols, mediumColsMode)) {
    return getGridColsClass(desktopCols, mobileCols)
  }

  const mediumCols =
    mediumColsMode === 'manual'
      ? normalizeGridColsNumber(colsNumberOnMedium || 0, 3)
      : getMediumGridColsNumber(cardsNumber, desktopCols, mobileCols)

  return [
    baseGridColsClasses[mobileCols],
    smGridColsClasses[mediumCols],
    lgGridColsClasses[desktopCols],
  ].join(' ')
}

export function getSlideWidth(cols: number, margin: number) {
  const gap = 40

  return `calc(${100 / cols}% - ${(gap / cols + margin).toFixed()}px)`
}

export const getInternalPath = (link: string) => {
  return link && link.trim?.().startsWith?.('/') ? link.trim() : undefined
}
